import React, { useState } from 'react';
import { Send, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';

export default function FeedbackForm() {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage(null);

    const formData = new FormData(e.currentTarget);
    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        setIsSubmitted(true);
        (e.target as HTMLFormElement).reset();
      } else {
        throw new Error('Failed to submit feedback. Please try again later.');
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setErrorMessage((error as Error).message);
    }
    setIsLoading(false);
  };

  return (
    <section className="py-20 bg-gradient-to-br from-indigo-50 to-purple-50">
      <div className="container mx-auto px-4">
        <motion.div
          className="max-w-2xl mx-auto bg-white p-8 rounded-2xl shadow-xl"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl font-bold text-center mb-2">Share Your Feedback</h2>
          <p className="text-gray-600 text-center mb-8">
            Help us improve Resumify with your valuable feedback.
          </p>

          {errorMessage && (
            <div className="mb-4 text-red-600 text-center">
              {errorMessage}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <input
              type="hidden"
              name="access_key"
              value="93a82eed-19c9-4729-9d58-e43dfa623022"
              aria-hidden="true"
            />

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label
                  className="block text-sm font-medium text-gray-700 mb-2"
                  htmlFor="name"
                >
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 
                    focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              <div>
                <label
                  className="block text-sm font-medium text-gray-700 mb-2"
                  htmlFor="email"
                >
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 
                    focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label
                className="block text-sm font-medium text-gray-700 mb-2"
                htmlFor="message"
              >
                Message
              </label>
              <textarea
                id="message"
                name="message"
                required
                rows={4}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 
                  focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            <motion.button
              type="submit"
              disabled={isLoading || isSubmitted}
              className={`w-full py-3 px-6 rounded-lg text-white font-medium flex items-center justify-center gap-2 transition-colors ${
                isSubmitted
                  ? 'bg-green-500'
                  : 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700'
              }`}
              whileHover={!isSubmitted ? { scale: 1.02 } : undefined}
              whileTap={!isSubmitted ? { scale: 0.98 } : undefined}
              aria-busy={isLoading}
              aria-disabled={isLoading || isSubmitted}
            >
              {isSubmitted ? (
                <>
                  <CheckCircle className="w-5 h-5" />
                  Feedback Sent
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  {isLoading ? 'Sending...' : 'Send Feedback'}
                </>
              )}
            </motion.button>
          </form>
        </motion.div>
      </div>
    </section>
  );
}
